import { useEffect, useMemo, useState } from 'react';
import { App, Alert, Button, Input, Modal, Select, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  SendOutlined,
} from '@ant-design/icons';
import type { CreateRfiRequest, RfiAsk } from '@/api/types';
import { createRfi, sendRfi } from '@/api/rfi';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { assembleBody, defaultSubject, realAsks } from './rfiTemplate';

const { Text, Paragraph } = Typography;

/** Communication channel families (the real binding + validation is server-side). */
const CHANNELS = [
  { value: 'EMAIL', label: 'Email' },
  { value: 'SECURE_PORTAL', label: 'Secure portal' },
  { value: 'SMS', label: 'SMS' },
  { value: 'POST', label: 'Postal' },
];

/** GA locales (en / hi / ar per F-IS-08) + common others. Delivery-side localisation is a server concern. */
const LOCALES = [
  { value: 'en', label: 'English (en)' },
  { value: 'hi', label: 'Hindi (hi)' },
  { value: 'ar', label: 'Arabic (ar)' },
  { value: 'fr', label: 'French (fr)' },
  { value: 'es', label: 'Spanish (es)' },
];

function emptyAsk(): RfiAsk {
  return { evidenceKind: '', note: '', precision: '' };
}

interface Props {
  open: boolean;
  entityId: string;
  workItemId?: string;
  onClose: () => void;
  /** Fired after a successful create/send so the RFI list refreshes. */
  onChanged?: () => void;
}

/**
 * RFI composer (F-IS-04). Deterministic client-side template (no AI drafting); the exact text shown
 * is the exact text sent. Two-step human gate: compose → review-and-send confirm.
 */
export function RfiComposer({ open, entityId, workItemId, onClose, onChanged }: Props) {
  const { message } = App.useApp();
  const [step, setStep] = useState<'compose' | 'review'>('compose');
  const [asks, setAsks] = useState<RfiAsk[]>([emptyAsk()]);
  const [channel, setChannel] = useState('EMAIL');
  const [locale, setLocale] = useState('en');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [subjectDirty, setSubjectDirty] = useState(false);
  const [bodyDirty, setBodyDirty] = useState(false);
  const [busy, setBusy] = useState<null | 'draft' | 'send'>(null);
  const [draftId, setDraftId] = useState<string | null>(null);

  // Reset when (re)opened.
  useEffect(() => {
    if (open) {
      setStep('compose');
      setAsks([emptyAsk()]);
      setChannel('EMAIL');
      setLocale('en');
      setSubject('');
      setBody('');
      setSubjectDirty(false);
      setBodyDirty(false);
      setBusy(null);
      setDraftId(null);
    }
  }, [open]);

  // Re-derive subject/body from asks until the analyst edits them (then leave their text alone).
  useEffect(() => {
    if (!subjectDirty) setSubject(defaultSubject(asks));
    if (!bodyDirty) setBody(assembleBody(asks));
  }, [asks, subjectDirty, bodyDirty]);

  const validAsks = useMemo(() => realAsks(asks), [asks]);
  const canProceed = validAsks.length > 0;

  function updateAsk(i: number, patch: Partial<RfiAsk>) {
    setAsks((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  }
  function addAsk() {
    setAsks((prev) => [...prev, emptyAsk()]);
  }
  function removeAsk(i: number) {
    setAsks((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));
  }

  function buildRequest(): CreateRfiRequest {
    return {
      entityId,
      workItemId,
      asks: validAsks,
      channel,
      locale,
      subject: subject.trim() || undefined,
      body: body.trim() || undefined,
    };
  }

  async function ensureDraft(): Promise<string | null> {
    if (draftId) return draftId;
    const res = await createRfi(buildRequest());
    setDraftId(res.rfiId);
    return res.rfiId;
  }

  async function onSaveDraft() {
    setBusy('draft');
    try {
      await ensureDraft();
      message.success('RFI saved as draft.');
      onChanged?.();
      onClose();
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) message.info('The RFI endpoint is not live yet (Track A pending).');
      else message.error(n.message);
    } finally {
      setBusy(null);
    }
  }

  async function onConfirmSend() {
    setBusy('send');
    try {
      const id = await ensureDraft();
      if (!id) return;
      await sendRfi(id);
      message.success('RFI sent.');
      onChanged?.();
      onClose();
    } catch (e) {
      const n = normalizeApiError(e);
      if (n.code === 'CHANNEL_UNBOUND') {
        message.error(
          'This hub has no bound provider for the selected channel — the draft was kept. Bind a communication provider or choose another channel.',
        );
      } else if (isNotAvailable(n)) {
        message.info('The RFI endpoint is not live yet (Track A pending).');
      } else {
        message.error(n.message);
      }
    } finally {
      setBusy(null);
    }
  }

  const composeBody = (
    <div>
      <div className="ol-section">Asks</div>
      {asks.map((a, i) => (
        <div className="ol-ask-row" key={i}>
          <div className="ol-ask-row__head">
            <span className="ol-ask-row__num">{i + 1}</span>
            <Input
              placeholder="Evidence kind (e.g. PROOF_OF_ADDRESS, BANK_STATEMENT)"
              value={a.evidenceKind}
              onChange={(e) => updateAsk(i, { evidenceKind: e.target.value })}
            />
            <Button
              type="text"
              size="small"
              aria-label={`Remove ask ${i + 1}`}
              icon={<DeleteOutlined />}
              disabled={asks.length === 1}
              onClick={() => removeAsk(i)}
            />
          </div>
          <Input.TextArea
            placeholder="What exactly do we need? (plain-language ask)"
            autoSize={{ minRows: 1, maxRows: 3 }}
            value={a.note}
            onChange={(e) => updateAsk(i, { note: e.target.value })}
            style={{ marginBottom: 6 }}
          />
          <Input
            placeholder="Precision hint (e.g. “dated within 90 days” — the semantic-gap detail)"
            value={a.precision}
            onChange={(e) => updateAsk(i, { precision: e.target.value })}
          />
        </div>
      ))}
      <Button icon={<PlusOutlined />} onClick={addAsk} size="small" style={{ marginBottom: 14 }}>
        Add ask
      </Button>

      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div className="ol-section">Channel</div>
          <Select value={channel} onChange={setChannel} options={CHANNELS} style={{ width: '100%' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div className="ol-section">Locale</div>
          <Select value={locale} onChange={setLocale} options={LOCALES} style={{ width: '100%' }} />
        </div>
      </div>

      <div className="ol-section" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>Draft (assembled from your asks — edit freely)</span>
        {(subjectDirty || bodyDirty) && (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto', fontSize: 11 }}
            onClick={() => {
              setSubjectDirty(false);
              setBodyDirty(false);
              setSubject(defaultSubject(asks));
              setBody(assembleBody(asks));
            }}
          >
            Reset from asks
          </Button>
        )}
      </div>
      <Input
        placeholder="Subject"
        value={subject}
        onChange={(e) => {
          setSubject(e.target.value);
          setSubjectDirty(true);
        }}
        style={{ marginBottom: 8 }}
      />
      <Input.TextArea
        value={body}
        onChange={(e) => {
          setBody(e.target.value);
          setBodyDirty(true);
        }}
        autoSize={{ minRows: 6, maxRows: 16 }}
      />
    </div>
  );

  const reviewBody = (
    <div>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 14, borderRadius: 8 }}
        message="Review before sending"
        description="This exact text will be sent — there is no AI drafting or rewriting after this point. Confirm the recipient-facing message below."
      />
      <div className="ol-kv" style={{ marginBottom: 12 }}>
        <span className="ol-kv__k">Entity</span>
        <span className="ol-kv__v ol-mono">{entityId}</span>
        <span className="ol-kv__k">Channel</span>
        <span className="ol-kv__v">{CHANNELS.find((c) => c.value === channel)?.label ?? channel}</span>
        <span className="ol-kv__k">Locale</span>
        <span className="ol-kv__v">{LOCALES.find((l) => l.value === locale)?.label ?? locale}</span>
        <span className="ol-kv__k">Asks</span>
        <span className="ol-kv__v">{validAsks.length}</span>
      </div>
      <div className="ol-section">Subject</div>
      <div className="ol-card" style={{ marginBottom: 10, fontWeight: 600 }}>
        {subject.trim() || <Text type="secondary">(no subject)</Text>}
      </div>
      <div className="ol-section">Body</div>
      <div className="ol-card">
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.55 }}>
          {body}
        </pre>
      </div>
    </div>
  );

  const footer =
    step === 'compose'
      ? [
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>,
          <Button key="draft" icon={<SaveOutlined />} loading={busy === 'draft'} onClick={onSaveDraft}>
            Save draft
          </Button>,
          <Button
            key="next"
            type="primary"
            disabled={!canProceed}
            onClick={() => setStep('review')}
          >
            Review &amp; send
          </Button>,
        ]
      : [
          <Button key="back" icon={<ArrowLeftOutlined />} onClick={() => setStep('compose')}>
            Back to edit
          </Button>,
          <Button key="draft" icon={<SaveOutlined />} loading={busy === 'draft'} onClick={onSaveDraft}>
            Save draft
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<SendOutlined />}
            loading={busy === 'send'}
            onClick={onConfirmSend}
          >
            Confirm &amp; send
          </Button>,
        ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={step === 'compose' ? 'Compose RFI' : 'Review & send RFI'}
      width={640}
      footer={footer}
      destroyOnClose
      maskClosable={false}
    >
      {step === 'compose' && !canProceed && (
        <Paragraph type="secondary" style={{ marginTop: 0, fontSize: 12.5 }}>
          Add at least one ask (an evidence kind or a note) to build the outreach.
        </Paragraph>
      )}
      {step === 'compose' ? composeBody : reviewBody}
    </Modal>
  );
}
